import React, {useEffect, useMemo, useState} from "react";
import Api from "../api";
import {
    Breadcrumb,
    DatePicker,
    Layout,
    message,
    Modal,
    Row,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";

const {Text} = Typography;
const {Content} = Layout;

const TABLE_HEADER_OFFSET = 300;

const LOG_LEVELS = {
    ERROR: "production.ERROR",
    INFO: "production.INFO",
    DEBUG: "production.DEBUG",
};

const LEVEL_LABELS = {
    [LOG_LEVELS.ERROR]: "Errors",
    [LOG_LEVELS.INFO]: "Info",
    [LOG_LEVELS.DEBUG]: "Debug",
};

const getStatusLabel = (status) => {
    switch (status) {
        case LOG_LEVELS.DEBUG:
            return <Tag color="gold">debug</Tag>;
        case LOG_LEVELS.INFO:
            return <Tag color="blue">info</Tag>;
        case LOG_LEVELS.ERROR:
            return <Tag color="red">error</Tag>;
        default:
            return <Tag>unknown</Tag>;
    }
};

const ErrorLog = () => {
    const [data, setData] = useState([]);
    const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(LOG_LEVELS.ERROR);
    const [tableHeight, setTableHeight] = useState(600);
    const [selectedEntry, setSelectedEntry] = useState(null);

    useEffect(() => {
        const updateTableHeight = () => {
            setTableHeight(Math.max(window.innerHeight - TABLE_HEADER_OFFSET, 320));
        };

        updateTableHeight();
        window.addEventListener("resize", updateTableHeight);

        return () => window.removeEventListener("resize", updateTableHeight);
    }, []);

    useEffect(() => {
        setLoading(true);

        Api.get("/admin/error-log?date=" + date)
            .then((res) => {
                setData(res.data.log.reverse());
                setLoading(false);
            })
            .catch(() => {
                setData([]);
                setLoading(false);
                message.error("Error log 404");
            });
    }, [date]);

    const counts = useMemo(() => ({
        [LOG_LEVELS.ERROR]: data.filter((item) => item.level === LOG_LEVELS.ERROR).length,
        [LOG_LEVELS.INFO]: data.filter((item) => item.level === LOG_LEVELS.INFO).length,
        [LOG_LEVELS.DEBUG]: data.filter((item) => item.level === LOG_LEVELS.DEBUG).length,
    }), [data]);

    const filteredData = useMemo(
        () => data.filter((item) => item.level === activeTab),
        [data, activeTab]
    );

    const columns = useMemo(() => [
        {
            title: "Timestamp",
            dataIndex: "timestamp",
            width: 100,
            render: (text) => <Text code>{moment(text).format("HH:mm:ss")}</Text>,
        },
        {
            title: "Level",
            dataIndex: "level",
            width: 90,
            render: (text) => getStatusLabel(text),
        },
        {
            title: "Message",
            dataIndex: "message",
            render: (text) => (
                <Text code className="error-log-message">
                    <span dangerouslySetInnerHTML={{__html: text}}/>
                </Text>
            ),
        },
    ], []);

    const tabItems = useMemo(() => [
        {
            key: LOG_LEVELS.ERROR,
            label: `${LEVEL_LABELS[LOG_LEVELS.ERROR]} (${counts[LOG_LEVELS.ERROR].toLocaleString()})`,
        },
        {
            key: LOG_LEVELS.INFO,
            label: `${LEVEL_LABELS[LOG_LEVELS.INFO]} (${counts[LOG_LEVELS.INFO].toLocaleString()})`,
        },
        {
            key: LOG_LEVELS.DEBUG,
            label: `${LEVEL_LABELS[LOG_LEVELS.DEBUG]} (${counts[LOG_LEVELS.DEBUG].toLocaleString()})`,
        },
    ], [counts]);

    return (
        <Content style={{margin: "0 16px"}}>
            <Breadcrumb style={{margin: "16px 0"}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Error log</Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <Row type="flex" align="middle" justify="space-between" wrap>
                    <DatePicker
                        value={dayjs(date, "YYYY-MM-DD")}
                        onChange={(_, dateString) => setDate(dateString)}
                    />
                    <Text type="secondary">
                        {filteredData.length.toLocaleString()} {LEVEL_LABELS[activeTab].toLowerCase()} entries
                    </Text>
                </Row>

                <Tabs
                    activeKey={activeTab}
                    items={tabItems}
                    onChange={setActiveTab}
                    style={{marginTop: 16}}
                />

                <Table
                    key={activeTab}
                    className="error-log-table"
                    rowKey={(record, index) => `${record.level}-${record.timestamp}-${index}`}
                    size="small"
                    virtual
                    loading={loading}
                    pagination={false}
                    scroll={{y: tableHeight}}
                    dataSource={filteredData}
                    columns={columns}
                    onRow={(record) => ({
                        onClick: () => setSelectedEntry(record),
                        style: {cursor: "pointer"},
                    })}
                />
            </div>

            <Modal
                open={!!selectedEntry}
                onCancel={() => setSelectedEntry(null)}
                footer={null}
                title={
                    selectedEntry
                        ? `${getStatusLabel(selectedEntry.level)} ${moment(selectedEntry.timestamp).format("YYYY-MM-DD HH:mm:ss")}`
                        : ""
                }
                width={860}
            >
                {selectedEntry && (
                    <div
                        className="error-log-detail"
                        dangerouslySetInnerHTML={{__html: selectedEntry.message}}
                    />
                )}
            </Modal>
        </Content>
    );
};

export default ErrorLog;
